// conversationsService.js

import axios from 'axios';
import store from '@/store';
import ConversationWindowDto from './Dto/ConversationWindowDto';
import { router } from '@/main';

const baseURL = `${process.env.VUE_APP_API_URL}`;
// const getAuthHeader = (token) => ({ Authorization: `Bearer ${token}` });
const getAuthHeader = (token) => {
    const devMode = store.state.user.devMode;

    if (devMode) {
        return { 'X-API-KEY': `${token}` };
    } else {
        return { Authorization: `Bearer ${token}` };
    }
};

let cancelToken = axios.CancelToken.source();

const apiRequest = async (method, url, data = null, params = {}, config = {}) => {
    if (store.getters['user/isInvalidLoggedInAccount']) {
        console.error('Account ID is not set');
        store.dispatch('user/logout');
        if (router.currentRoute.value.path !== '/') {
            router.push({ name: 'Login', query: { logout: 'true' } });
        }
        return;
    }
    try {
        return store.dispatch('user/apiCall', async (token) => {
            const requestConfig = {
                method: method,
                url: `${baseURL}/accounts/${store.state.user.user.account_id}/${url}`,
                headers: getAuthHeader(token),
                params: params instanceof URLSearchParams ? params : { ...params },
                data: data,
                ...config, // Merge additional config (including signal or cancelToken)
            };

            return await axios(requestConfig);
        });
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log('Request canceled: ', error.message);
        } else {
            throw error;
        }
    }
};

const apiRequestv2 = async (method, url, data = null, params = {}, config = {}) => {
    if (store.getters['user/isInvalidLoggedInAccount']) {
        console.error('Account ID is not set');
        store.dispatch('user/logout');
        if (router.currentRoute.value.path !== '/') {
            router.push({ name: 'Login', query: { logout: 'true' } });
        }
        return;
    }
    try {
        return store.dispatch('user/apiCall', async (token) => {
            const requestConfig = {
                method: method,
                url: `${baseURL}/v2/accounts/${store.state.user.user.account_id}/${url}`,
                headers: getAuthHeader(token),
                params: params instanceof URLSearchParams ? params : { ...params },
                data: data,
                ...config, // Merge additional config (including signal or cancelToken)
            };

            return await axios(requestConfig);
        });
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log('Request canceled: ', error.message);
        } else {
            throw error;
        }
    }
};

const conversationsService = {
    fetchRooms() {
        return apiRequest('get', 'conversations');
    },

    fetchConversations(page = 0, size = 20, timestamp = null, filters = null, archived = false, config = {}) {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('size', size);
        params.append('archived', archived);
        if (timestamp) params.append('timestamp', timestamp);

        if (filters) {
            Object.entries(filters).forEach(([key, values]) => {
                // Ensure values is always an array
                const valuesArray = Array.isArray(values) ? values : [values];

                if (key === 'excludedTags') {
                    valuesArray.forEach((value) => {
                        params.append('filters.excludedTag', value);
                    });
                } else {
                    valuesArray.forEach((value) => {
                        params.append(`filters.${key}`, value);
                    });
                }
            });
        }

        return apiRequest('get', 'conversations', null, params, config);
    },

    getConversation(conversationId) {
        return apiRequest('get', `conversations/${conversationId}`);
    },

    async fetchMessages(conversationId, page = 0, size = 20) {
        const params = { page, size };
        const response = await apiRequest('get', `conversations/${conversationId}/messages`, null, params);
        // for (const message of response.data.content) {
        //     if (message.metadata) {
        //         console.log('Message:', message);
        //         const metadata = JSON.parse(message.metadata);
        //         console.log('Metadata:', metadata);
        //     }
        // }
        return response;
    },

    // Fotos, vídeos, documentos y audios de la conversación, lo más reciente primero.
    fetchMediaMessages(conversationId, page = 0, size = 30) {
        return apiRequest('get', `conversations/${conversationId}/media`, null, { page, size });
    },

    getMessage(messageId) {
        return apiRequest('get', `messages/${messageId}`);
    },

    sendMessage(messageData) {
        return apiRequest('post', 'messages', messageData);
    },

    fetchOrders(customerId, conversationId) {
        return apiRequest('get', `customers/${customerId}/orders`, null, { conversation: conversationId });
    },

    fetchAssets(customerId, abortController = null) {
        const config = abortController ? { signal: abortController.signal } : {};
        return apiRequestv2('get', `customers/${customerId}/assets`, null, {}, config);
    },

    async searchRooms(pattern, page = 0, size = 20, sort = 'dateCreated,desc') {
        try {
            // Handle the '#' character in the pattern
            let encodedSearchPattern = pattern;
            if (pattern.startsWith('#')) {
                const searchPattern = pattern.substring(1);
                encodedSearchPattern = `%23${encodeURIComponent(searchPattern)}`;
            }

            // Cancel the previous request if it exists
            if (cancelToken) {
                cancelToken.cancel('Operation canceled due to new request.');
            }
            cancelToken = axios.CancelToken.source();

            const params = new URLSearchParams();
            params.append('page', page);
            params.append('size', size);
            params.append('pattern', encodedSearchPattern);

            if (sort) {
                const sortArray = Array.isArray(sort) ? sort : [sort];
                sortArray.forEach((s) => {
                    params.append('sort', s);
                });
            }

            return apiRequest('get', 'conversations/search', null, params, { cancelToken: cancelToken.token });
        } catch (error) {
            throw error;
        }
    },

    getNotesOrder(orderName) {
        return apiRequest('get', `orders/${orderName}/notes`);
    },

    getAssetNotes(customerId, assetName) {
        return apiRequest('get', `customers/${customerId}/assets/${assetName}/notes`);
    },

    updateStateAI(conversationId, aiEnabled) {
        const data = { ai_enabled: aiEnabled, conversation: conversationId };
        return apiRequest('post', `conversations/${conversationId}/ai-configuration`, data);
    },

    getProductInfo(productId) {
        return apiRequest('get', `catalogs/${store.state.user.user.account_id}/items/${productId}/info`);
    },

    async getStorageURL(channelId, typeFile, fileName) {
        try {
            const uniqueName = `${new Date().getTime()}-${fileName}`;

            const response = await apiRequest('get', 'messages/media/upload-url', null, {
                channelId,
                contentType: typeFile,
                fileName: uniqueName,
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    clearConversation(conversationId) {
        return apiRequest('post', `conversations/${conversationId}/clean`);
    },

    scaleAgent(agent, conversationId) {
        const reqBody = {
            account: store.state.user.user.account_id,
            conversation: conversationId,
            ai_type: agent,
        };

        return apiRequest('post', `conversations/${conversationId}/scale-agent`, reqBody);
    },

    getCopilotCorrection(conversation_id, message, previousMessage = null, config = {}) {
        const reqData = {
            account_id: store.state.user.user.account_id,
            conversation_id,
            message,
            context: previousMessage ? previousMessage : message,
        };
        return apiRequest('post', 'messages/copilot', reqData, null, config);
    },

    setConversationNotes(conversationId, note) {
        const reqData = {
            note,
        };

        return apiRequest('post', `conversations/${conversationId}/note`, reqData);
    },

    setConversationAsRead(conversationId) {
        const reqData = {
            account_id: store.state.user.user.account_id,
            conversation_id: conversationId,
            read: true,
        };
        return apiRequest('post', `conversations/${conversationId}/mark-as-read`, reqData);
    },

    setConversationAsUnread(conversationId) {
        const reqData = {
            account_id: store.state.user.user.account_id,
            conversation_id: conversationId,
            read: false,
        };
        return apiRequest('post', `conversations/${conversationId}/mark-as-unread`, reqData);
    },

    setConversationAsArchived(conversationId) {
        const reqData = {
            account_id: store.state.user.user.account_id,
            conversation_id: conversationId,
        };
        return apiRequest('post', `conversations/${conversationId}/mark-as-archived`, reqData);
    },

    setConversationAsUnarchived(conversationId) {
        const reqData = {
            account_id: store.state.user.user.account_id,
            conversation_id: conversationId,
        };
        return apiRequest('post', `conversations/${conversationId}/mark-as-unarchived`, reqData);
    },

    updateConversationTags(conversationId, tags) {
        const reqData = {
            tags: [...tags],
        };
        return apiRequest('post', `conversations/${conversationId}/tags`, reqData);
    },

    /**
     * Get a list of conversation windows
     * @param {string} conversationId - The ID of the conversation to retrieve
     * @returns {Promise<ConversationWindowDto[]>} The requested conversation windows
     */
    async getConversationWindows(conversationId) {
        const response = await apiRequest('get', `conversations/${conversationId}/conversation-windows`);
        return (response?.data || []).map((window) =>
            new ConversationWindowDto.Builder()
                .withUuid(window.uuid)
                .withIsClosed(window.is_closed)
                .withAccount(window.account)
                .withInvoiced(window.invoiced)
                .withConversation(window.conversation)
                .withSentiment(window.sentiment)
                .withHumanParticipation(window.human_participation)
                .withSummary(window.summary)
                .withSummaryTranslated(window.summary_translated)
                .withSummaryDateCreated(window.summary_date_created)
                .withHasSummary(typeof window.has_summary === 'boolean' ? window.has_summary : Boolean(window.summary))
                .withTopic(window.topic)
                .withChannelType(window.channel_type)
                .withOwner(window.owner)
                .withTags(window.tags)
                .withOpenedByCampaign(window.opened_by_campaign)
                .withDateCreated(window.date_created)
                .withDateClosed(window.date_closed)
                .build(),
        );
    },

    /**
     * Todos los mensajes de una ventana, en orden (sin paginar). El hilo del inbox puede
     * no tenerla entera cargada; el panel de reporte la necesita completa.
     * @param {string} windowUuid
     * @returns {Promise<Object[]>} Mensajes en el formato crudo de la API
     */
    async getConversationWindowMessages(windowUuid) {
        const response = await apiRequest('get', `conversations-windows/${windowUuid}/messages`);
        return Array.isArray(response?.data) ? response.data : [];
    },

    /**
     * Pide al agente que genere el resumen de una ventana que no lo tiene, y lo
     * persiste. Sólo válido para ventanas cerradas.
     *
     * El resumen se genera SIEMPRE en inglés: es el mismo texto que luego se
     * inyecta al agente, que razona en inglés.
     * @param {string} conversationId
     * @param {string} windowUuid
     * @returns {Promise<{summary: string}|null>}
     */
    /**
     * @param {boolean} [refresh] true para actualizar un resumen que se cree caducado.
     *        El backend sólo comprueba la caducidad cuando esto viene a true, para no
     *        pagar una consulta extra en cada lectura.
     */
    async generateConversationWindowSummary(conversationId, windowUuid, refresh = false) {
        const suffix = refresh ? '?refresh=true' : '';
        const response = await apiRequest(
            'post',
            `conversations/${conversationId}/conversation-windows/${windowUuid}/summary${suffix}`,
        );
        return response?.data ?? null;
    },

    async scaleConversationToUser(conversationId, userId) {
        const reqData = {
            account: store.state.user.user.account_id,
            conversation: conversationId,
            ai_type: 'HUMAN_AGENT',
            user_id: userId,
        };
        return apiRequest('post', `conversations/${conversationId}/scale-agent`, reqData);
    },

    updateCustomerName(conversationId, customerId, customerName) {
        const reqData = {
            customer_id: customerId,
            customer_name: customerName,
        };
        return apiRequest('post', `conversations/${conversationId}/update-customer-name`, reqData);
    },

    /**
     * Get all conversations for a given customer
     * @param {string} customerId - The customer ID
     * @returns {Promise<Object>} List of conversations
     */
    getConversationsByCustomer(customerId) {
        return apiRequest('get', `customers/${customerId}/conversations`);
    },

    /**
     * Get a time-limited signed URL for a call recording.
     * @param {string} callId - The call ID
     * @returns {Promise<Object>} The response containing the signed URL
     */
    getCallRecordingUrl(callId) {
        return apiRequest('get', `calls/${callId}/recording-url`);
    },

    /**
     * Get transcription messages for a completed call
     * @param {string} callId - The call ID
     * @returns {Promise<Object>} List of call messages
     */
    getCallMessages(callId) {
        return apiRequest('get', `calls/${callId}/messages`);
    },
};
export default conversationsService;
